import {
  Box,
  Flex,
  IconButton,
  Link,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Input, Switch } from '@gamma/ui/form-fields';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import _ from 'lodash';
import {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Controller, useForm, ControllerRenderProps } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { URLS } from '../../../../shared/constants/constants';
import { ExporterType } from '../../../../shared/generated/graphql';
import {
  ElasticsearchBulk,
  ElasticsearchExporter,
  ElasticsearchExporterAuth,
  ExporterTLSInfo,
  useSaveExporter,
} from '../../queries';

import VisibilityOffSharpIcon from '@gamma/ui/icons/VisibilityOffSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers';
import { useSetExporterFormState } from '../../hooks/useSetExporterFormState';
import { validateExporterUrl } from '../../../../shared/utils/validateExporterUrl';
import { EXPORTER_DEFAULTS } from '../../pages/IntegrationsConstants';
import { ExporterSslSwitch, OnChangeSwitchProps } from '../ExporterSslSwitch';

const { ELASTIC_SEARCH_DOCS_HTTP_EXPORTERS } = URLS;

interface ElasticsearchFormProps {
  isUpdating: boolean;
  deleteLoading: boolean;
  onDeleteOpen: () => void;
  clearInProgress: () => void;
  activeExporter: ElasticsearchExporter | null;
  setIsUpdating: (isUpdating: boolean) => void;
  setIsSaveLoading: (isSaveLoading: boolean) => void;
  setIsSaveDisabled: (isSaveDisabled: boolean) => void;
  setIsFormUnchanged: (isFormUnchanged: boolean) => void;
  setIsCancelDisabled: (isCancelDisabled: boolean) => void;
  setFormRef: (formRef: RefObject<HTMLFormElement> | null) => void;
  refetchExporters: () => Promise<unknown>;
}

interface AlertExportData {
  name: string;
  enabled: boolean;
  auth: ElasticsearchExporterAuth;
  bulk: ElasticsearchBulk;
  endpoint: string;
  tls: ExporterTLSInfo;
}

export const ElasticSearchForm = ({
  isUpdating,
  setFormRef,
  onDeleteOpen,
  deleteLoading,
  setIsUpdating,
  activeExporter,
  clearInProgress,
  setIsSaveLoading,
  setIsSaveDisabled,
  setIsFormUnchanged,
  setIsCancelDisabled,
  refetchExporters,
}: ElasticsearchFormProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<AlertExportData>({
    mode: 'onChange',
    defaultValues: {
      ...EXPORTER_DEFAULTS,
      auth: {
        user: '',
        password: '',
      },
      bulk: {
        index: '',
      },
      endpoint: '',
    },
  });

  const { enabled: enabledLabel, disabled: disabledLabel } =
    i18n.pages.integrations;

  const { forms, elasticsearchForm, toast } = i18n.pages.alertExport;

  const { elasticDocumentation, elasticDocumentationLink } = elasticsearchForm;

  const { saveSuccessDesc, saveSuccessTitle, saveFailDesc, saveFailTitle } =
    toast;

  const navigate = useNavigate();
  const showToastMessage = useToast();
  const { user } = useContext(AuthContext);
  const tenant = user?.attributes?.['custom:tenant_id'];

  const formData = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const [exportConfig, setExportConfig] = useState<
    AlertExportData | ElasticsearchExporter | null
  >(activeExporter as ElasticsearchExporter);
  const [shouldDisableSave, setShouldDisableSave] = useState(true);
  const [showAuthPasswordType, setShowAuthPasswordType] =
    useState<boolean>(false);

  const { name, enabled, tls, endpoint, auth, bulk } = exportConfig || {};

  const fillForm = useCallback(() => {
    setValue('name', name || '');
    setValue('enabled', enabled || false);
    setValue('tls', tls || EXPORTER_DEFAULTS.tls);
    setValue('endpoint', endpoint || '');
    setValue(
      'auth',
      auth || {
        user: '',
        password: '',
      },
    );
    setValue(
      'bulk',
      bulk || {
        index: '',
      },
    );
  }, [setValue, name, enabled, tls, endpoint, bulk, auth]);

  useEffect(() => {
    setExportConfig(activeExporter as ElasticsearchExporter);
  }, [activeExporter]);

  useEffect(() => {
    fillForm();
  }, [activeExporter, exportConfig, fillForm]);

  const [saveExporter, { loading: saveLoading, error: _saveError }] =
    useSaveExporter({
      onCompleted: async (data) => {
        const { saveExporter: savedExporter } = data;
        const exporter_id = savedExporter?.exporter_id;

        // Refetch triggers cache eviction and data refresh in parent
        await refetchExporters();

        setShouldDisableSave(true);
        setIsUpdating(false);

        showToastMessage({
          status: 'success',
          title: saveSuccessTitle,
          description: saveSuccessDesc,
          isClosable: true,
          position: 'bottom-right',
        });
        clearInProgress();
        if (exporter_id) {
          navigate(exporter_id);
        }
      },
      onError: () => {
        setShouldDisableSave(false);
        setIsUpdating(false);
        showToastMessage({
          status: 'error',
          title: saveFailTitle,
          description: saveFailDesc,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const formIsPopulated =
    formData.name !== '' &&
    formData.endpoint !== '' &&
    formData.bulk.index !== '' &&
    formData.auth.user !== '' &&
    formData.auth.password !== '';

  const formIsUnchanged = _.isEqual(formData, {
    name,
    enabled,
    endpoint,
    tls,
    bulk,
    auth,
  });

  const isSaveDisabled = formIsUnchanged || !formIsPopulated || saveLoading;

  useEffect(() => {
    setShouldDisableSave(isSaveDisabled);
  }, [isSaveDisabled]);

  const submitAlertExport = async (data: AlertExportData) => {
    setShouldDisableSave(false);
    setIsUpdating(true);
    setExportConfig(data);
    const { name, enabled, tls, auth, bulk, endpoint } = data;
    const input = {
      tenant: tenant || '',
      name,
      enabled,
      exporter: ExporterType.Elasticsearch,
      elasticsearch: {
        auth: {
          user: auth.user,
          password: auth.password,
        },
        bulk: {
          index: bulk.index,
        },
        endpoint,
        tls: {
          verify_certificate: tls.verify_certificate,
        },
      },
      exporter_id: activeExporter?.exporter_id || null,
    };
    saveExporter({
      variables: {
        input,
      },
    });
  };

  const togglePasswordType = () => {
    if (isUpdating) return;
    setShowAuthPasswordType(!showAuthPasswordType);
  };

  useSetExporterFormState({
    formRef,
    isUpdating,
    saveLoading,
    deleteLoading,
    isSaveDisabled,
    formIsUnchanged,
    shouldDisableSave,
    setFormRef,
    setIsSaveLoading,
    setIsSaveDisabled,
    setIsFormUnchanged,
    setIsCancelDisabled,
  });

  return (
    <>
      <VStack my={2} spacing={4} alignItems="start">
        <Text textStyle="body-md">
          {elasticDocumentation}{' '}
          <Link
            isExternal
            color="blue.400"
            textDecoration="none"
            href={ELASTIC_SEARCH_DOCS_HTTP_EXPORTERS}
          >
            {elasticDocumentationLink}
          </Link>
          .
        </Text>
      </VStack>
      <VStack
        as="form"
        spacing={4}
        ref={formRef}
        align="flex-start"
        id="alertExportForm"
        onSubmit={handleSubmit(submitAlertExport)}
      >
        <Controller
          name="enabled"
          control={control}
          render={({
            field: { onChange, name, value },
          }: {
            field: ControllerRenderProps<AlertExportData, 'enabled'>;
          }) => (
            <Box w="100%">
              <Switch
                inline
                name={name}
                isChecked={value}
                onChange={onChange}
                data-testid="exportSwitch"
                isDisabled={isUpdating}
                label={formData.enabled ? enabledLabel : disabledLabel}
              />
            </Box>
          )}
        />
        <Flex w="100%">
          <Input
            {...register('name', { required: true })}
            isRequired
            label={forms.exporterName}
            isDisabled={isUpdating}
          />
        </Flex>
        <VStack w="100%" spacing={1} align="stretch">
          {errors.endpoint && (
            <Text color="red.500" fontSize="sm">
              {errors.endpoint.message || 'Invalid URL format'}
            </Text>
          )}
          <Input
            {...register('endpoint', {
              required: true,
              validate: (value) => validateExporterUrl(value),
            })}
            isRequired
            label={forms.url}
            isDisabled={isUpdating}
          />
        </VStack>
        <Flex w="100%">
          <Input
            {...register('auth.user', { required: true })}
            isRequired
            label={forms.username}
            isDisabled={isUpdating}
          />
        </Flex>
        <Flex w="100%">
          <Input
            {...register('auth.password', {
              required: true,
            })}
            isRequired
            label={forms.password}
            isDisabled={isUpdating}
            type={showAuthPasswordType ? 'text' : 'password'}
            rightElement={
              <IconButton
                cursor="pointer"
                isDisabled={isUpdating}
                aria-label={`${
                  showAuthPasswordType ? 'Hide' : 'Show'
                } Password`}
                onClick={togglePasswordType}
              >
                {showAuthPasswordType ? (
                  <VisibilityOffSharpIcon size="sm" />
                ) : (
                  <VisibilitySharpIcon size="sm" />
                )}
              </IconButton>
            }
          />
        </Flex>
        <Flex w="100%">
          <Input
            {...register('bulk.index', { required: true })}
            isRequired
            label={forms.index}
            isDisabled={isUpdating}
          />
        </Flex>
        <Flex w="100%" alignItems="end" justifyContent="space-between">
          <Controller
            name="tls"
            control={control}
            render={({
              field: { onChange, name, value },
            }: {
              field: ControllerRenderProps<AlertExportData, 'tls'>;
            }) => (
              <ExporterSslSwitch
                name={name}
                value={value}
                isUpdating={isUpdating}
                onChange={(props: OnChangeSwitchProps) => {
                  onChange({
                    ...tls,
                    verify_certificate: props.verify_certificate,
                  });
                  setValue('tls', {
                    ...tls,
                    verify_certificate: props.verify_certificate,
                  });
                }}
              />
            )}
          />
          {!activeExporter?.inProgress && (
            <IconButton
              size="sm"
              variant="solid"
              colorScheme="red"
              aria-label={forms.deleteBtn}
              isDisabled={saveLoading || deleteLoading}
              data-testid="alert-exporter-delete-button"
              onClick={() => {
                setIsUpdating(true);
                onDeleteOpen();
              }}
              icon={
                deleteLoading ? (
                  <Spinner size={'xs'} />
                ) : (
                  <DeleteSharpIcon boxSize={5} />
                )
              }
            />
          )}
        </Flex>
      </VStack>
    </>
  );
};
